import { type ChangeEvent, useCallback } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFormContext, useWatch } from 'react-hook-form';
import { match, P } from 'ts-pattern';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { type InputDropdownOption, SelectInput } from '@interxfi/design-system/components/input';
import type {
  IxmUserauthV1ProgramRegistrationType,
  IxmUserauthV1ProgramRegistrationTypeInvestmentAdviser,
  IxmUserauthV1ProgramRegistrationTypeOneOfValue,
} from '@interxfi/types/generated/api';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import { AppTextInputWithLens } from '../../../../../components/form/app-text-input';
import { addDefinedValues } from '../../../../../util/has-value';

const brokerDealerRegistrationTypeSchema = z.object({
  kind: z.literal('brokerDealer'),
  value: z.object({
    crdNumber: z
      .string()
      .nonempty('CRD Number is required')
      .regex(/^[0-9]{2,6}$/, 'CRD Number must be between 2 and 6 digits'),
    secRegistrationNumber: z
      .string()
      .nonempty('SEC Registration Number is required')
      .regex(/^[0-9]{1,3}-[0-9]{5,6}$/, 'SEC Registration Number must be in the format XXX-XXXXX'),
  }),
});

const investmentAdviserRegistrationTypeSchema = z.object({
  kind: z.literal('investmentAdviser'),
  value: z.object({
    crdNumber: z
      .string()
      .nonempty('CRD Number is required')
      .regex(/^[0-9]{2,6}$/, 'CRD Number must be between 2 and 6 digits'),
    secRegistrationNumber: z
      .string()
      .regex(/^[0-9]{1,3}-[0-9]{5,6}$/, 'SEC Registration Number must be in the format XXX-XXXXX')
      .or(z.literal('')),
  }),
});

const noRegistrationTypeSchema = z.object({
  kind: z.literal('none'),
});

export const programRegistrationTypeFormSchema = z.discriminatedUnion('kind', [
  brokerDealerRegistrationTypeSchema,
  investmentAdviserRegistrationTypeSchema,
  noRegistrationTypeSchema,
]);

export type ProgramRegistrationTypeFormValues = z.infer<typeof programRegistrationTypeFormSchema>;

export const defaultProgramRegistrationTypeFormValues: ProgramRegistrationTypeFormValues = { kind: 'none' };

export function getInitialFormValuesForProgramRegistrationType(data: IxmUserauthV1ProgramRegistrationType | undefined) {
  return match(data)
    .returnType<ProgramRegistrationTypeFormValues>()
    .with({ brokerDealer: P.not(P.nullish) }, (r) => ({
      kind: 'brokerDealer',
      value: { crdNumber: r.brokerDealer?.crdNumber || '', secRegistrationNumber: r.brokerDealer?.secRegistrationNumber || '' },
    }))
    .with({ investmentAdviser: P.not(P.nullish) }, (r) => ({
      kind: 'investmentAdviser',
      value: { crdNumber: r.investmentAdviser?.crdNumber || '', secRegistrationNumber: r.investmentAdviser?.secRegistrationNumber || '' },
    }))
    .with(P.union(undefined, { none: P.not(P.nullish) }), () => ({ kind: 'none' }))
    .exhaustive(() => ({ kind: 'none' }));
}

export function formValuesToProgramRegistrationType(values: ProgramRegistrationTypeFormValues): IxmUserauthV1ProgramRegistrationType {
  return match(values)
    .returnType<IxmUserauthV1ProgramRegistrationType>()
    .with({ kind: 'brokerDealer' }, (v) => ({
      brokerDealer: {
        crdNumber: v.value.crdNumber,
        secRegistrationNumber: v.value.secRegistrationNumber,
      },
    }))
    .with({ kind: 'investmentAdviser' }, (v) => ({
      investmentAdviser: addDefinedValues<IxmUserauthV1ProgramRegistrationTypeInvestmentAdviser>(
        {
          crdNumber: v.value.crdNumber,
        },
        {
          secRegistrationNumber: v.value.secRegistrationNumber || undefined,
        },
      ),
    }))
    .with({ kind: 'none' }, () => ({ none: {} }))
    .exhaustive();
}

const TYPE_OPTIONS = [
  {
    label: 'None',
    value: 'none',
  },
  {
    label: 'Broker-Dealer',
    value: 'brokerDealer',
  },
  {
    label: 'Investment Adviser',
    value: 'investmentAdviser',
  },
] as const satisfies InputDropdownOption<IxmUserauthV1ProgramRegistrationTypeOneOfValue>[];

interface ProgramRegistrationTypeFormProps {
  lens: Lens<ProgramRegistrationTypeFormValues>;
}

export function ProgramRegistrationTypeSelectionForm({ lens }: ProgramRegistrationTypeFormProps) {
  const { setValue } = useFormContext();
  const { name } = lens.interop();
  const kindInterop = lens.focus('kind').interop();
  const typeSelection = useWatch({ ...kindInterop, defaultValue: 'none' });
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      setValue(
        name,
        match(e.target.value as ProgramRegistrationTypeFormValues['kind'])
          .with('brokerDealer', () => ({ kind: 'brokerDealer', value: { crdNumber: '', secRegistrationNumber: '' } }))
          .with('investmentAdviser', () => ({ kind: 'investmentAdviser', value: { crdNumber: '', secRegistrationNumber: '' } }))
          .otherwise(() => ({ kind: 'none' })),
        { shouldValidate: true },
      );
    },
    [name, setValue],
  );

  return (
    <SelectInput
      label="Registration Type"
      name={kindInterop.name}
      onChange={handleChange}
      options={TYPE_OPTIONS}
      required
      value={typeSelection || 'none'}
    />
  );
}

export function ProgramRegistrationTypeDataForm({ lens }: ProgramRegistrationTypeFormProps) {
  const typeSelection = useWatch({ ...lens.focus('kind').interop(), defaultValue: 'none' });

  switch (typeSelection) {
    case 'brokerDealer':
      return (
        <VerticalStack gap={4}>
          <AppTextInputWithLens label="CRD Number" lens={lens.focus('value.crdNumber')} required />
          <AppTextInputWithLens label="SEC Registration Number" lens={lens.focus('value.secRegistrationNumber')} required />
        </VerticalStack>
      );
    case 'investmentAdviser':
      return (
        <VerticalStack gap={4}>
          <AppTextInputWithLens label="CRD Number" lens={lens.focus('value.crdNumber')} required />
          <AppTextInputWithLens label="SEC Registration Number" lens={lens.focus('value.secRegistrationNumber')} />
        </VerticalStack>
      );
    case 'none':
      return null;
    default:
      exhaustiveCheck(typeSelection);
      return null;
  }
}
